// src/modals/ProfileModal.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  MapPin,
  MessageCircle,
  Heart,
  Users,
  Flag,
  ShieldOff,
  UserPlus,
  Sparkles,
  Ruler,
  GraduationCap,
  Briefcase,
  Globe,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useModal } from '../hooks';
import { INTENTIONS, INTERESTS, EDUCATION_LEVELS, LANGUAGES, LOOKING_FOR } from '../constants';
import { getDisplayAge, getCommonInterests, calculateCompatibility } from '../utils';
import conversationService from '../services/conversationService';
import userService from '../services/userService';
import { Btn, Badge, ConfirmModal } from '../components/ui';
import ReportModal from './ReportModal';
import PajNtaubBand from '../components/ui/PajNtaubBand';

const ProfileModal = ({
  isOpen,
  onClose,
  profile: p,
  onLike,
  hasLiked,
  toast,
  currentUser,
  onBlock,
  context = 'dating',
  onFriendAction,
  friendshipStatus = 'none',
}) => {
  const { onBackdrop } = useModal(isOpen, onClose);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sendingMsg, setSendingMsg] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [blocking, setBlocking] = useState(false);

  if (!isOpen || !p) return null;

  const isOwnProfile = currentUser?.uid === p.uid;

  const clanCompatible =
    p.clan &&
    currentUser?.clan &&
    p.clan !== currentUser.clan &&
    p.clan !== 'Autre' &&
    currentUser.clan !== 'Autre';

  const commonValues = new Set(getCommonInterests(currentUser, p, INTERESTS).map((c) => c.value));
  const isDatingCtx = context === 'dating';
  const compat = isDatingCtx ? calculateCompatibility(currentUser, p) : null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 animate-fade-in"
      onClick={onBackdrop}
    >
      <div
        data-testid="profile-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Profil de ${p?.name || ''}`}
        className="bg-white dark:bg-gray-900 w-full sm:max-w-2xl rounded-t-2xl sm:rounded-xl max-h-[85dvh] flex flex-col overflow-hidden animate-slide-up"
      >
        <PajNtaubBand height={4} />

        {/* Conteneur scrollable unique — couverture + avatar + contenu défilent ensemble */}
        <div className="flex-1 min-h-0 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
          {/* Couverture — pas de overflow-hidden */}
          <div className="relative h-32 sm:h-40 bg-gradient-to-r from-rose-500 to-fuchsia-500">
            {p.coverPicture && (
              <img
                src={p.coverPicture}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            <div className="absolute top-3 right-3 flex gap-2">
              {!isOwnProfile && (
                <button
                  onClick={() => setShowReport(true)}
                  data-testid="profile-modal-report"
                  aria-label={t('profileModal.ariaReport')}
                  className="w-9 h-9 rounded-full bg-black/30 text-white flex items-center justify-center hover:bg-black/50 transition"
                >
                  <Flag size={16} />
                </button>
              )}
              <button
                onClick={onClose}
                aria-label={t('profileModal.ariaClose')}
                className="w-9 h-9 rounded-full bg-black/30 text-white flex items-center justify-center hover:bg-black/50 transition"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Avatar — frère de la couverture, z-10 pour passer au-dessus */}
          <div className="px-4 -mt-10 relative z-10">
            <img
              src={p.profilePicture}
              alt={p.name}
              className="w-20 h-20 rounded-full border-4 border-white dark:border-gray-900 object-cover bg-gray-200 dark:bg-gray-800"
            />
          </div>

          {/* Nom, ville, clan */}
          <div className="px-4 sm:px-5 mt-2 pb-4 sm:pb-5">
            <div className="flex justify-between items-start gap-2 mb-4">
              <div className="min-w-0">
                <h2 className="text-base sm:text-xl font-semibold dark:text-gray-100">
                  {p.name}
                  {getDisplayAge(p) !== null ? `, ${getDisplayAge(p)}` : ''}
                </h2>
                <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 text-sm">
                  <MapPin size={14} />
                  {p.city}
                </div>
              </div>
              {p.clan && <Badge c="rose">{p.clan}</Badge>}
            </div>

            <div className="flex gap-1.5 mb-4">
              {p.badges?.map((b, i) => (
                <Badge key={i} c="gray">
                  {b}
                </Badge>
              ))}
            </div>

            {/* Score compatibilité (dating only) */}
            {isDatingCtx && compat && compat.score > 70 && (
              <div className="mb-4">
                <span
                  data-testid="profile-modal-compat"
                  className="inline-flex items-center gap-1 bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white text-xs font-bold px-3 py-1.5 rounded-full"
                >
                  <Sparkles size={14} />
                  {t('profileModal.compatible', { score: compat.score })}
                </span>
              </div>
            )}

            {/* lookingFor + intention (dating only) */}
            {isDatingCtx && (
              <div className="mb-4 grid grid-cols-2 gap-2">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('profileModal.lookingFor')}
                  </p>
                  <p className="font-semibold text-sm dark:text-gray-200">
                    {p.lookingFor
                      ? t(LOOKING_FOR.find((o) => o.value === p.lookingFor)?.labelKey)
                      : t('profileModal.notSpecified')}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('profileModal.intention')}
                  </p>
                  <p className="font-semibold text-sm dark:text-gray-200">
                    {t(
                      INTENTIONS.find((i) => i.value === p.intention)?.labelKey ??
                        'profileModal.notSpecified'
                    )}
                  </p>
                </div>
              </div>
            )}

            {/* Champs dating enrichis (dating only) */}
            {isDatingCtx &&
              ((p.height && p.height >= 100 && p.height <= 250) || p.education || p.profession) && (
                <div className="mb-4 grid grid-cols-2 gap-2">
                  {p.height && p.height >= 100 && p.height <= 250 && (
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 flex items-center gap-2">
                      <Ruler size={14} className="text-gray-400 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t('profileModal.height')}
                        </p>
                        <p className="font-semibold text-sm dark:text-gray-200">{p.height} cm</p>
                      </div>
                    </div>
                  )}
                  {p.education && (
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 flex items-center gap-2">
                      <GraduationCap size={14} className="text-gray-400 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t('profileModal.education')}
                        </p>
                        <p className="font-semibold text-sm dark:text-gray-200">
                          {t(EDUCATION_LEVELS.find((o) => o.value === p.education)?.labelKey)}
                        </p>
                      </div>
                    </div>
                  )}
                  {p.profession && (
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 flex items-center gap-2">
                      <Briefcase size={14} className="text-gray-400 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t('profileModal.profession')}
                        </p>
                        <p className="font-semibold text-sm dark:text-gray-200">{p.profession}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

            {/* Langues (visible dans les deux modes) */}
            {p.languages && p.languages.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <Globe size={14} className="text-gray-400" />
                  <h3 className="font-bold text-sm dark:text-gray-200">
                    {t('profileModal.languages')}
                  </h3>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {p.languages.map((v) => {
                    const found = LANGUAGES.find((l) => l.value === v);
                    return (
                      <Badge key={v} c="purple">
                        {found ? t(found.labelKey) : v}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mb-4">
              <h3 className="font-bold text-sm mb-2 dark:text-gray-200">
                {t('profileModal.about')}
              </h3>
              {p.bioPrompts && p.bioPrompts.length > 0 ? (
                <div className="space-y-2">
                  {p.bioPrompts.map((bp, idx) => (
                    <div key={idx} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold mb-1">
                        {t(bp.prompt)}
                      </p>
                      <p className="text-gray-600 dark:text-gray-300 text-sm">{bp.answer}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-300 text-sm bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  {p.bio || t('profileModal.noBio')}
                </p>
              )}
            </div>

            {p.interests && p.interests.length > 0 && (
              <div className="mb-4">
                <h3 className="font-bold text-sm mb-2 dark:text-gray-200">
                  {t('profileModal.interests')}
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {p.interests.map((val) => {
                    const found = INTERESTS.find((i) => i.value === val);
                    const label = found ? t(found.labelKey) : val;
                    const isCommon = commonValues.has(val);
                    return (
                      <span
                        key={val}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                          isCommon
                            ? 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        }`}
                      >
                        {label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {clanCompatible && (
              <div className="bg-purple-50 dark:bg-purple-950/30 rounded-xl p-3">
                <div className="flex gap-2">
                  <Users size={18} className="text-purple-500" />
                  <div>
                    <p className="font-semibold text-purple-700 dark:text-purple-400 text-sm">
                      {t('profileModal.clanCompatible')}
                    </p>
                    <p className="text-xs text-purple-600 dark:text-purple-400">
                      {t('profileModal.clanCompatibleDesc')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions — sticky en bas, toujours visible */}
        <div className="flex-shrink-0 p-3 sm:p-4 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 flex gap-3">
          {!isOwnProfile && (
            <Btn
              v="ghost"
              icon={ShieldOff}
              data-testid="profile-modal-block"
              onClick={() => setShowBlockConfirm(true)}
              aria-label={t('profileModal.ariaBlock')}
              className="text-gray-400 hover:text-red-500"
            />
          )}
          {isDatingCtx ? (
            <>
              {(currentUser?.matches || []).includes(p.uid) && (
                <Btn
                  v="outline"
                  className="flex-1"
                  icon={MessageCircle}
                  data-testid="profile-modal-message"
                  loading={sendingMsg}
                  onClick={async () => {
                    setSendingMsg(true);
                    const r = await conversationService.createConversation(
                      currentUser.uid,
                      p.uid,
                      'match'
                    );
                    setSendingMsg(false);
                    if (r.success) {
                      onClose();
                      navigate(`/messages/${r.conversation.id}`);
                    } else {
                      console.error('ProfileModal message error:', r);
                      toast && toast(t('profileModal.toastMessageError'), 'error');
                    }
                  }}
                >
                  {t('profileModal.messageBtn')}
                </Btn>
              )}
              <Btn
                className="flex-1"
                icon={Heart}
                onClick={onLike}
                v={hasLiked ? 'ghost' : 'primary'}
                data-testid="profile-modal-like"
              >
                {hasLiked ? t('profileModal.liked') : t('profileModal.likeBtn')}
              </Btn>
            </>
          ) : (
            <>
              {friendshipStatus === 'none' && onFriendAction && (
                <Btn
                  className="flex-1"
                  icon={UserPlus}
                  onClick={() => onFriendAction('send', p.uid)}
                  data-testid="profile-modal-add-friend"
                >
                  {t('profileModal.addFriend')}
                </Btn>
              )}
              {friendshipStatus === 'friends' && (
                <Btn
                  v="outline"
                  className="flex-1"
                  icon={MessageCircle}
                  data-testid="profile-modal-friend-message"
                  onClick={() => {
                    onClose();
                    const convId = `friend_${[currentUser.uid, p.uid].sort().join('_')}`;
                    navigate(`/messages/${convId}`);
                  }}
                >
                  {t('profileModal.messageBtn')}
                </Btn>
              )}
              {friendshipStatus === 'pending_sent' && (
                <Btn v="ghost" className="flex-1" disabled>
                  {t('profileModal.pendingSent')}
                </Btn>
              )}
            </>
          )}
        </div>
      </div>

      <ReportModal
        isOpen={showReport}
        onClose={() => setShowReport(false)}
        reportedId={p.uid}
        reportedName={p.name}
        currentUserId={currentUser?.uid}
        toast={toast}
      />

      <ConfirmModal
        isOpen={showBlockConfirm}
        onClose={() => setShowBlockConfirm(false)}
        title={t('profileModal.blockTitle', { name: p.name })}
        message={t('profileModal.blockMessage')}
        confirmLabel={t('profileModal.blockConfirm')}
        confirmVariant="danger"
        loading={blocking}
        onConfirm={async () => {
          setBlocking(true);
          const r = await userService.blockUser(currentUser.uid, p.uid);
          setBlocking(false);
          if (r.success) {
            toast && toast(t('profileModal.toastBlocked', { name: p.name }), 'success');
            setShowBlockConfirm(false);
            onClose();
            if (onBlock) onBlock(p.uid);
          } else {
            toast && toast(t('profileModal.toastError'), 'error');
          }
        }}
      />
    </div>
  );
};

export default ProfileModal;
